import { Box, Button, Container, ExpandableSection, Icon } from '@amzn/awsui-components-react';

import { constants } from '../../common/Localization';
import PreviewBanner from '../common/PreviewBanner';

interface Props {
    dependencyType: string;
    dependencyItems: any[];
    onFilter: (filterBy: string) => void;
}

export enum ResourceTypes {
    APPLICATION_DATABASE = 'application-db',
    APPLICATION_SERVER = 'application-server',
}

type ResourceConfig = {
    [key: string]: {
        resourceName: string;
        filterByField: string;
    }
} 
const RESOURCE_TYPE_TO_CONFIG: ResourceConfig = {
    [ResourceTypes.APPLICATION_DATABASE]: {
        resourceName: constants.DATABASES,
        filterByField: 'databaseMapped',
    },
    [ResourceTypes.APPLICATION_SERVER]: {
        resourceName: constants.SERVERS,
        filterByField: 'serverMapped',
    },
};

function DependencyValidationBox({ dependencyItems, dependencyType, onFilter }: Props): JSX.Element | null {
    const [unknownResources, unknownApplications] = dependencyItems.reduce(([unknownResources, unknownApplications], item) => {
        if (dependencyType === ResourceTypes.APPLICATION_SERVER && item.serverMapped === constants.UNMAPPED
            || dependencyType === ResourceTypes.APPLICATION_DATABASE && item.databaseMapped === constants.UNMAPPED) {
            unknownResources++;
        }
        if (item.applicationMapped === constants.UNMAPPED) {
            unknownApplications++;
        }
        return [unknownResources, unknownApplications];
    }, [0, 0]);

    const getHeader = () => {
        if (!unknownResources && !unknownApplications) {
            return (
                <Box>
                    <Icon name='status-positive' variant='success'/>
                    <Box margin={{left: 'xs'}} display='inline' variant='h4'>
                        {constants.ALL_DEPENDENCY_PASSED_VALIDATION}
                    </Box>
                </Box>
            );
        } else {
            return (
                <Box>
                    <Icon name='status-warning' variant='error'/>
                    <Box margin={{left: 'xs'}} display='inline' variant='h4'>
                        {constants.ERRORS_FOUND_HEADER_0 + (unknownResources + unknownApplications) + constants.VALIDATION_ERRORS_IN_DEPENDENCY_DATA}
                    </Box>
                    <Box padding={{left: 'l'}}>{constants.USE_FOLLOWING_FILTERS_TO_CORRECT_INVALID_VALUES}</Box>
                </Box>
            );
        }
    };

    const getExpandableSectionClassNames = (): string => {
        if (!unknownResources && !unknownApplications) {
            return 'data-validation-alert all-valid';
        } else {
            return 'data-validation-alert invalid';
        }
    };

    const contentHeaderRow = (
        <li className='validation-head' key='header'>
            <div />
            <div>{constants.VALIDATION_RULE}</div>
            <div>{constants.TOTAL_ISSUES}</div>
        </li>
    );
    const applicationRow = unknownApplications ? (
        <li className='validation-row'>
            <div />
            <div>{constants.UNKNOWN} {constants.APPLICATIONS}
                <Button variant='inline-icon' iconName='filter' onClick={() => onFilter('applicationMapped')}></Button>
            </div>
            <div>{unknownApplications}</div>
        </li>
    ) : null;
    const resourceRow = unknownResources ? (
        <li className='validation-row'>
            <div />
            <div>{constants.UNKNOWN} {RESOURCE_TYPE_TO_CONFIG[dependencyType].resourceName}  
                <Button variant='inline-icon' iconName='filter' onClick={() => onFilter(RESOURCE_TYPE_TO_CONFIG[dependencyType].filterByField)}></Button>
            </div>
            <div>{unknownResources}</div>
        </li>
    ) : null;
    return (
        <Container disableContentPaddings>
            <PreviewBanner customHeader={constants.DATA_QUALITY_PREVIEW_HEADER} />
            <ExpandableSection
                header={getHeader()}
                className={getExpandableSectionClassNames()}
                variant='footer'
                defaultExpanded
            >
                <Box margin={{ top: 'm' }} className='rule-content'> 
                    <ul className='validation-table'>
                        {contentHeaderRow}
                        {applicationRow}
                        {resourceRow}
                    </ul>
                </Box>
            </ExpandableSection>
        </Container>
    );
}

export default DependencyValidationBox;
